import type { CellValue } from "@zebrabi/office-types";
import { persistManagerInstance } from "@zebrabi/office-settings/PersistManager";
import { KNOWLEDGE_BASE_URL, TEMPLATES_URL } from "@zebrabi/office-licensing/constants";
import { getOfficeSettings } from "@zebrabi/office-settings";
import { getExcelTable, getSampleDataRangeStartRowAndColumn } from "@zebrabi/data-helpers/excelDataHelpers";
import { valuesTranslations } from "@zebrabi/data-helpers/translations";
import { DATA_SOURCE, DataSource, EMPTY_WORKSHEET_MSG, PARSING_ERROR_MSG, SHOW_CHOOSER_LINK_ID } from "@zebrabi/data-helpers/editData";
import { InsertType } from "@zebrabi/analytics-tracking/definitions";
import { trackUsedDataSource } from "@zebrabi/analytics-tracking/sourceDataTracking";

/** The fields the charts (categorical) and tables (matrix) DataView types share. */
export interface OfficeDataView {
    rowFields: Excel.PivotField[] | string[];
    columnFields: Excel.PivotField[] | string[];
    valueFields: Excel.PivotField[] | string[];
    rowGrandTotal: boolean;
    columnGrandTotal: boolean;
    errorMessage?: string;
    rowNames: string[];
    columnNames: string[];
    valueNames: string[];
}

/** Everything the data helper needs from the visual that owns it. */
export interface DataHelperHost<TDataView extends OfficeDataView = OfficeDataView> {
    /** The visual's current DataView, undefined until the first one is set. */
    currentDataView: () => TDataView | undefined;
    /** Fired after the bound Excel data changed and the stored data source was refreshed. */
    onDataChanged: () => void;
}

/** The ids of the workbook bindings through which the helper follows changes to the source pivot table or range. */
export interface DataBindingIds {
    pivotTable: string;
    range: string;
}

export type { CellValue } from "@zebrabi/office-types";

/** A row written back to the worksheet; empty cells are `null`. */
export type WorksheetRow = (CellValue | null)[];

/** How many fields a pivot table has in each of its areas. */
export interface PivotFieldCounts {
    rows: number;
    columns: number;
    values: number;
}

/** A pivot table's fields, layout flags and cell values, loaded and ready to be shaped into a DataView. */
export interface PivotTableInput {
    rowFields: string[];
    columnFields: string[];
    valueFields: string[];
    rowGrandTotal: boolean;
    columnGrandTotal: boolean;
    rowLabelsValues: CellValue[][];
    columnLabelsValues: CellValue[][];
    dataValues: CellValue[][];
    layout: Excel.PivotLayout;
}

/** An Excel range with its header row and category columns loaded, ready to be shaped into a DataView. */
export interface RangeInput {
    context: Excel.RequestContext;
    range: Excel.Range;
    /** The first row of the range as read: the category headers, then the measure headers. */
    headerRow: CellValue[];
    categoryColumnsCount: number;
    /** The category columns without the header row; date cells are already formatted as text. */
    categoryLabelsValues: CellValue[][];
}

/**
 * The Office data access shared by the charts and tables add-ins: resolving and persisting the data source, reading
 * ranges, Excel tables and pivot tables, following bound data changes, and the parsing error messages. A visual
 * extends it with the conversion of the loaded cells into its own DataView type.
 */
export abstract class OfficeDataHelper<
    TDataView extends OfficeDataView,
    THost extends DataHelperHost<TDataView> = DataHelperHost<TDataView>,
> {
    private dataChangeHandlerReference: OfficeExtension.EventHandlerResult<Excel.BindingDataChangedEventArgs>;
    private storedDataSource: DataSource;

    constructor(protected readonly host: THost) {}

    /** The visual's path segment in the knowledge base and template links. */
    protected abstract readonly visualName: string;

    /** The chooser's name in the parsing error message, which links to it. */
    protected abstract readonly galleryName: string;

    /** Opens the visual's chooser when the add-in is inserted on an empty sheet. */
    protected abstract mountChooser(): void;

    /** Binding ids are persisted in the workbook, so each visual keeps the ones its documents already carry. */
    protected abstract readonly bindingIds: DataBindingIds;

    protected abstract getEmptyDataView(msg: string): TDataView;

    /** The error message for a pivot table whose field layout the visual cannot show, null when it can. */
    protected abstract validatePivotFields(counts: PivotFieldCounts): string | null;

    protected abstract parsePivotTable(input: PivotTableInput): TDataView;

    /** How many leading columns of the range hold categories. */
    protected abstract countCategoryColumns(range: Excel.Range): number;

    /** Shapes the range into the visual's DataView. An errorMessage on the result skips the data-changed binding. */
    protected abstract parseRange(input: RangeInput): TDataView | Promise<TDataView>;

    /** The rows the visual's DataView writes back to the worksheet as sample data: a header row, then one row per category. */
    protected abstract dataViewToRows(dataView: TDataView): WorksheetRow[];

    public async getDataView(): Promise<TDataView | null | undefined> {
        if (Office.context.host === Office.HostType.PowerPoint) {
            return this.host.currentDataView(); // This is updated in visual.ts subscribeToXSpreadSheetData
        }
        if (Office.context.host === Office.HostType.Excel) {
            this.storedDataSource = this.getDataSource();
            if (!this.storedDataSource) {
                //check for empty sheet
                const isEmpty = await this.checkForEmptyWorksheet();
                if (isEmpty) {
                    this.mountChooser();
                    return this.getEmptyDataView(this.getDataParsingErrorMsgHtml(true));
                }
            }
            return this.readExcelData();
        }
        throw new Error("Unsupported Office app");
    }

    public async checkForEmptyWorksheet(): Promise<boolean> {
        return Excel.run(async (context) => {
            const activeWorksheet = context.workbook.worksheets.getActiveWorksheet();
            const usedRangeOrNull = activeWorksheet.getUsedRangeOrNullObject(true);
            await context.sync();
            return usedRangeOrNull.isNullObject;
        }).catch((error) => {
            console.log("Excel.Run error: " + error);
            if (error instanceof OfficeExtension.Error) {
                console.log("Debug info: " + JSON.stringify(error.debugInfo));
            }
            return true;
        });
    }

    public async writeDataViewToWorksheet() {
        await Excel.run(async (context) => {
            const activeWorksheet = context.workbook.worksheets.getActiveWorksheet();
            const dataView = this.host.currentDataView();
            if (!dataView) {
                return;
            }
            const data = this.dataViewToRows(dataView);

            const { startRow, startColumn } = await getSampleDataRangeStartRowAndColumn(activeWorksheet, context, data.length);

            const range = activeWorksheet.getRangeByIndexes(startRow, startColumn, data.length, data[0].length);
            range.values = data;
            range.select();
            const table = activeWorksheet.tables.add(range, true);

            activeWorksheet.load({ id: true });
            table.load({ id: true, name: true });

            await context.sync();

            this.saveDataSource({ table: table.name, tableId: table.id, worksheetId: activeWorksheet.id });
        }).catch((error) => {
            console.log("Excel.Run error: " + error);
            if (error instanceof OfficeExtension.Error) {
                console.log("Debug info: " + JSON.stringify(error.debugInfo));
            }
        });
    }

    /* https://docs.microsoft.com/en-us/office/dev/add-ins/excel/excel-add-ins-pivottables#pivottable-layouts-and-getting-pivoted-data */
    private async getPivotTableDataView(
        pivotTable: Excel.PivotTable,
        context: Excel.RequestContext,
        activeWorksheet: Excel.Worksheet,
        saveToSettings: boolean
    ): Promise<TDataView> {
        let dataView = this.getEmptyDataView("");
        try {
            const bodyFullRange = pivotTable.layout.getRange();
            this.addDataChangeHandler(context, bodyFullRange, Excel.BindingType.range, this.bindingIds.pivotTable);

            const rowHierarchies = pivotTable.rowHierarchies;
            const columnHierarchies = pivotTable.columnHierarchies;
            const dataHierarchies = pivotTable.dataHierarchies;

            const rowHierarchiesCount = rowHierarchies.getCount();
            const columnHierarchiesCount = columnHierarchies.getCount();
            const dataHierarchiesCount = dataHierarchies.getCount();
            await context.sync();

            const fieldsError = this.validatePivotFields({
                rows: rowHierarchiesCount.value,
                columns: columnHierarchiesCount.value,
                values: dataHierarchiesCount.value,
            });
            if (fieldsError) {
                dataView.errorMessage = fieldsError;
                return dataView;
            }

            pivotTable.load({ name: true });
            pivotTable.layout.load({ showRowGrandTotals: true, showColumnGrandTotals: true, subtotalLocation: true });

            // loading items/fields crashes Excel in some cases, so only id and name are loaded for fields detection
            rowHierarchies.load({ id: true, name: true });
            columnHierarchies.load({ id: true, name: true });
            dataHierarchies.load({ id: true, name: true });

            await context.sync();

            const valuesCol = columnHierarchies.items.find((item) => valuesTranslations.includes(item.id));
            const columnFields = columnHierarchies.items.filter((item) => item.id !== valuesCol?.id).map((c) => c.name); // "Values" column is filtered out since it is not used as Grouping column
            const rowFields = rowHierarchies.items.map((item) => item.name);
            const valueFields = dataHierarchies.items.map((item) => item.name);

            const dataBodyRange = pivotTable.layout.getDataBodyRange();
            const rowLabelsRange = pivotTable.layout.getRowLabelRange();
            const columnLabelsRange = pivotTable.layout.getColumnLabelRange();
            dataBodyRange.load({ values: true });
            rowLabelsRange.load({
                values: true,
                valueTypes: true,
                rowCount: true,
                columnCount: true,
                numberFormat: true,
                numberFormatCategories: true,
            });
            columnLabelsRange.load({ values: true });

            await context.sync();

            const rowLabelsValues = (rowLabelsRange?.values as CellValue[][] | undefined) ?? [];
            this.checkRowsLabelsDataTypeForDates(rowLabelsRange, rowLabelsValues);

            dataView = this.parsePivotTable({
                rowFields,
                columnFields,
                valueFields,
                rowGrandTotal: pivotTable.layout.showRowGrandTotals,
                columnGrandTotal: pivotTable.layout.showColumnGrandTotals,
                rowLabelsValues,
                columnLabelsValues: columnLabelsRange.values as CellValue[][],
                dataValues: dataBodyRange.values as CellValue[][],
                layout: pivotTable.layout,
            });

            if (saveToSettings) {
                this.saveDataSource({ pivotTable: pivotTable.name, worksheetId: activeWorksheet.id });
                void this.trackDataSource(context, InsertType.PivotTable, dataBodyRange);
            }
        } catch (error) {
            console.log(error);
            dataView.errorMessage = "pivot table data could not be parsed correctly";
        }

        return dataView;
    }

    private async getDataViewFromRange(context: Excel.RequestContext, sourceRange: Excel.Range): Promise<TDataView | null> {
        try {
            const range = sourceRange;
            range.load({ address: true, cellCount: true, columnCount: true, rowCount: true, values: true, valueTypes: true });

            const firstRow = range.getRow(0);
            firstRow.load({ values: true });

            await context.sync();

            const categoryColumnsCount = this.countCategoryColumns(range);

            const categoryLabelsRange = range.getAbsoluteResizedRange(range.rowCount, categoryColumnsCount);
            categoryLabelsRange.load({ columnCount: true, numberFormatCategories: true, rowCount: true, values: true, valueTypes: true });

            await context.sync();

            const categoryLabelsValues = categoryLabelsRange.values as CellValue[][];
            this.checkRowsLabelsDataTypeForDates(categoryLabelsRange, categoryLabelsValues);
            categoryLabelsValues.shift(); // remove the header row

            const dataView = await this.parseRange({
                context,
                range,
                headerRow: firstRow.values[0] as CellValue[],
                categoryColumnsCount,
                categoryLabelsValues,
            });

            if (!dataView.errorMessage) {
                this.addDataChangeHandler(context, range, Excel.BindingType.range, this.bindingIds.range);
            }

            return dataView;
        } catch (error) {
            console.log("range parsing error");
            console.error(error);
            return null;
        }
    }

    private async readExcelData(): Promise<TDataView | null> {
        return Excel.run(async (context) => {
            let activeWorksheet = context.workbook.worksheets.getActiveWorksheet();
            activeWorksheet.load({ id: true, name: true });
            await context.sync();

            // check for saved data source
            const storedDataSource = this.storedDataSource;
            if (storedDataSource) {
                // create existing add-in from saved data source
                if (storedDataSource.pivotTable) {
                    const storedPivotTable = context.workbook.pivotTables.getItemOrNullObject(storedDataSource.pivotTable);
                    const ws = storedDataSource.worksheetId
                        ? context.workbook.worksheets.getItemOrNullObject(storedDataSource.worksheetId)
                        : null;
                    await context.sync();
                    if (!storedPivotTable.isNullObject) {
                        //Hack: it looks like that for the multi level row hierarcihies pivot table has to be read from worksheet and not workbook
                        // TODO: check if this is still needed (might be related to ZVE-1139)
                        if (ws && !ws.isNullObject) {
                            const wsPivotTable = ws.pivotTables.getItemOrNullObject(storedDataSource.pivotTable);
                            await context.sync();
                            if (!wsPivotTable.isNullObject) {
                                return await this.getPivotTableDataView(wsPivotTable, context, ws, false);
                            }
                        }
                        return await this.getPivotTableDataView(storedPivotTable, context, activeWorksheet, false);
                    }
                    return this.getEmptyDataView(
                        this.getDataParsingErrorMsgHtml()
                            + " Error details: pivot table used to create this add-in not found: "
                            + storedDataSource.pivotTable
                    );
                }

                // check if we should use active worksheet for reading data, desktop versions loads add-ins differenty then online so using active worksheet is NOT always correct
                if (storedDataSource.worksheetId && storedDataSource.worksheetId !== activeWorksheet.id) {
                    const dataSourceWs = context.workbook.worksheets.getItemOrNullObject(storedDataSource.worksheetId);
                    await context.sync();
                    if (!dataSourceWs.isNullObject) {
                        activeWorksheet = dataSourceWs;
                    }
                } else if (!storedDataSource.worksheetId && storedDataSource.range) {
                    const storedWsName = storedDataSource.range.split("!", 2)[0];
                    if (storedDataSource.range && activeWorksheet.name !== storedWsName) {
                        activeWorksheet = context.workbook.worksheets.getItemOrNullObject(storedWsName);
                    }
                }

                const sourceDataRange = await this.getStoredDataSourceRange(context, activeWorksheet, storedDataSource);
                if (sourceDataRange) {
                    const rangeDataView = await this.getDataViewFromRange(context, sourceDataRange);
                    return rangeDataView
                        ? rangeDataView
                        : this.getEmptyDataView(
                              this.getDataParsingErrorMsgHtml()
                                  + " Error details: error reading data from saved data source "
                                  + JSON.stringify(storedDataSource)
                          );
                }
                return this.getEmptyDataView(
                    this.getDataParsingErrorMsgHtml() + " Error details: could not parse " + JSON.stringify(storedDataSource)
                );
            }

            // inserting a new add-in
            const selectedRange = await this.getSelectedRange(context);

            if (selectedRange === null) {
                return await this.getEmptySelectionDataView(context, activeWorksheet, null);
            } else {
                const pivotTables = selectedRange.getPivotTables();
                const pivotTablesCount = pivotTables.getCount();
                await context.sync();
                if (pivotTablesCount.value > 0) {
                    return await this.getPivotTableDataView(pivotTables.getFirst(), context, activeWorksheet, true);
                }

                const tables = selectedRange.getTables();
                const tablesCount = tables.getCount();
                await context.sync();
                if (tablesCount.value > 0) {
                    const table = tables.getFirst();
                    return await this.getExcelTableDataView(table, context, activeWorksheet, true);
                }

                // insert from suitable Range
                selectedRange.load({ values: true });
                await context.sync();
                const isEmptyCellSelected = selectedRange.values[0][0] === "" && selectedRange.cellCount === 1;

                if (isEmptyCellSelected) {
                    return await this.getEmptySelectionDataView(context, activeWorksheet, selectedRange);
                } else {
                    if (selectedRange.cellCount < 6) {
                        // try get surrounding range
                        const surroundingRange = await this.getSurroundingNonBlankRange(context, activeWorksheet, selectedRange);
                        if (surroundingRange) {
                            const rangeDataView = await this.getDataViewFromRange(context, surroundingRange);
                            if (rangeDataView) {
                                this.saveDataSource({ range: surroundingRange.address, worksheetId: activeWorksheet.id });
                                void this.trackDataSource(context, InsertType.Range, surroundingRange);
                                return rangeDataView;
                            }
                        }
                    } else {
                        // use selected range
                        const rangeDataView = await this.getDataViewFromRange(context, selectedRange);
                        if (rangeDataView) {
                            this.saveDataSource({ range: selectedRange.address, worksheetId: activeWorksheet.id });
                            void this.trackDataSource(context, InsertType.Range, selectedRange);
                            return rangeDataView;
                        }
                    }

                    void this.trackDataSource(context, InsertType.Range, selectedRange);

                    return this.getEmptyDataView(
                        this.getDataParsingErrorMsgHtml() + " Error details: error reading data from selected range"
                    );
                }
            }
        }).catch((error) => {
            console.log("readData Excel.Run error: " + error);
            if (error instanceof OfficeExtension.Error) {
                console.log("Debug info: " + JSON.stringify(error.debugInfo));
            }
            return null;
        });
    }

    private async getEmptySelectionDataView(
        context: Excel.RequestContext,
        activeWorksheet: Excel.Worksheet,
        selectedRange: Excel.Range
    ): Promise<TDataView> {
        const tableCount = activeWorksheet.tables.getCount();
        const pivotTableCount = activeWorksheet.pivotTables.getCount();
        const pivotTablesCollection = activeWorksheet.pivotTables.load({ id: true, name: true });
        const tableCollection = activeWorksheet.tables;
        await context.sync();

        if (pivotTableCount.value > 0) {
            const pivotTable = pivotTablesCollection.items[0];
            return await this.getPivotTableDataView(pivotTable, context, activeWorksheet, true);
        } else if (tableCount.value > 0) {
            const table = tableCollection.getItemAt(0);
            return await this.getExcelTableDataView(table, context, activeWorksheet, true);
        } else {
            // try to use used range otherwise show chooser
            if (selectedRange) {
                const surroundingRange = await this.getSurroundingNonBlankRange(context, activeWorksheet, selectedRange);
                if (surroundingRange) {
                    const rangeDataView = await this.getDataViewFromRange(context, surroundingRange);
                    if (rangeDataView) {
                        this.saveDataSource({ range: surroundingRange.address, worksheetId: activeWorksheet.id });
                        void this.trackDataSource(context, InsertType.Range, surroundingRange);
                        return rangeDataView;
                    }
                }
            }

            const usedRange = activeWorksheet.getUsedRangeOrNullObject(true);
            await context.sync();
            if (!usedRange.isNullObject) {
                const rangeDataView = await this.getDataViewFromRange(context, usedRange);
                if (rangeDataView) {
                    this.saveDataSource({ range: usedRange.address, worksheetId: activeWorksheet.id });
                    void this.trackDataSource(context, InsertType.Range, usedRange);
                    return rangeDataView;
                }
            }

            this.mountChooser();
            void this.trackDataSource(context, InsertType.Range, selectedRange);
            return this.getEmptyDataView(this.getDataParsingErrorMsgHtml(true) + " Error details: no suitable data source found.");
        }
    }

    private async getExcelTableDataView(
        table: Excel.Table,
        context: Excel.RequestContext,
        activeWorksheet: Excel.Worksheet,
        saveToSettings: boolean
    ): Promise<TDataView> {
        table.load({ id: true, name: true });
        await context.sync();

        const rangeDataView = await this.getDataViewFromRange(context, table.getRange());
        if (rangeDataView && saveToSettings) {
            this.saveDataSource({ table: table.name, tableId: table.id, worksheetId: activeWorksheet.id });
            void this.trackDataSource(context, InsertType.Table, table.getRange());
            return rangeDataView;
        }

        void this.trackDataSource(context, InsertType.Table, table.getRange());

        return this.getEmptyDataView(this.getDataParsingErrorMsgHtml() + " Error details: could not parse Excel Table " + table.name);
    }

    private async getStoredDataSourceRange(
        context: Excel.RequestContext,
        activeWorksheet: Excel.Worksheet,
        dataSource: DataSource
    ): Promise<Excel.Range | null> {
        let range: Excel.Range = null;
        try {
            if (dataSource && dataSource.table) {
                range = (await getExcelTable(context, activeWorksheet, dataSource.table, dataSource.tableId)).getRange();
                await context.sync();
            } else if (dataSource && dataSource.range) {
                const savedSourceAddress: string = dataSource.range.toString();
                const savedWsRange = savedSourceAddress.split("!", 2);
                // use only range address without worksheet name
                const sourceRange = savedWsRange.length === 2 ? savedWsRange[1] : dataSource.range;
                // read data from stored range
                range = activeWorksheet.getRange(sourceRange);
                await context.sync();
            }

            return range;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    private async getSelectedRange(context: Excel.RequestContext): Promise<Excel.Range | null> {
        let selectedRange: Excel.Range = null;
        try {
            selectedRange = context.workbook.getSelectedRange();
            selectedRange.load({ address: true, cellCount: true });
            //TODO: check if selectedRange.worksheet === activeWorksheet ?
            await context.sync();
        } catch (e) {
            console.log("error parsing Selected range:", e);
            selectedRange = null;
        }
        return selectedRange;
    }

    private async getSurroundingNonBlankRange(
        context: Excel.RequestContext,
        activeWorksheet: Excel.Worksheet,
        selectedCell: Excel.Range
    ): Promise<Excel.Range | null> {
        try {
            const usedRange = activeWorksheet.getUsedRangeOrNullObject(true);
            await context.sync();
            if (usedRange.isNullObject) {
                return null;
            }

            const range = selectedCell.getSurroundingRegion();
            range.load({ address: true });
            await context.sync();
            range.select();
            return range;
        } catch (e) {
            console.log("error parsing Selected range:", e);
            return null;
        }
    }

    private getDataSource(): DataSource {
        return <DataSource>getOfficeSettings(DATA_SOURCE);
    }

    protected saveDataSource(dataSource: DataSource) {
        Office.context.document.settings.set(DATA_SOURCE, dataSource);
        persistManagerInstance.update({
            objectName: DATA_SOURCE,
            properties: dataSource,
        });
    }

    protected async trackDataSource(context: Excel.RequestContext, insertType: InsertType, range: Excel.Range) {
        try {
            range.load({ values: true });
            await context.sync();
            const sourceData = range.values;
            void trackUsedDataSource(insertType, sourceData);
        } catch (error) {
            console.error("data source used err", error);
        }
    }

    protected addDataChangeHandler(context: Excel.RequestContext, range: Excel.Range, bindingType: Excel.BindingType, id: string) {
        const binding = context.workbook.bindings.add(range, bindingType, id);
        if (!this.dataChangeHandlerReference) {
            this.dataChangeHandlerReference = binding.onDataChanged.add(this.dataChangedEventHandler.bind(this));
        }
    }

    private async dataChangedEventHandler(event: Excel.BindingDataChangedEventArgs) {
        await Excel.run(async (context) => {
            await context.sync();
            if (this.dataChangeHandlerReference) {
                this.dataChangeHandlerReference.remove();
                await this.dataChangeHandlerReference.context.sync();
                this.dataChangeHandlerReference = null;
            }

            const binding = context.workbook.bindings.getItemOrNullObject(event.binding.id);
            await context.sync();
            // update stored range if it has changed, this handles inserts/deletes of Excel rows/columns
            if (!binding.isNullObject) {
                this.storedDataSource = this.getDataSource();
                if (this.storedDataSource?.range) {
                    const bindingRange = binding.getRange();
                    bindingRange.load({ address: true });
                    await context.sync();
                    const boundRangeAddress = bindingRange.address;

                    if (boundRangeAddress !== this.storedDataSource.range) {
                        this.storedDataSource.range = boundRangeAddress;
                        this.saveDataSource(this.storedDataSource);
                    }
                }
                binding.delete();
            }

            await context.sync();
            this.host.onDataChanged();

            return context.sync();
        }).catch((error) => {
            console.log("dataChangedEvent error: " + error);
            if (error instanceof OfficeExtension.Error) {
                console.log("Debug info: " + JSON.stringify(error.debugInfo));
            }
        });
    }

    protected checkRowsLabelsDataTypeForDates(rowLabelsRange: Excel.Range, rowLabelsValues: CellValue[][]) {
        for (let i = 0; i < rowLabelsRange.rowCount; i++) {
            for (let j = 0; j < rowLabelsRange.columnCount; j++) {
                const valueType = rowLabelsRange.valueTypes[i][j];
                const numberFormatCategory = rowLabelsRange.numberFormatCategories[i][j];
                if (
                    (valueType === Excel.RangeValueType.double || valueType === Excel.RangeValueType.integer)
                    && (numberFormatCategory === Excel.NumberFormatCategory.custom
                        || numberFormatCategory === Excel.NumberFormatCategory.date)
                ) {
                    const val = rowLabelsValues[i][j];
                    if (typeof val === "number") {
                        rowLabelsValues[i][j] = new Date(Date.UTC(0, 0, val - 1)).toLocaleDateString();
                    }
                }
            }
        }
    }

    protected hasCommentsDataCol(valueTypes: Excel.RangeValueType[][]): boolean {
        if (!valueTypes || valueTypes[0]?.length < 2) {
            return false;
        }

        let hasCommentsCol = false;
        // iterate 2. to last col
        for (let j = 1; j < valueTypes[0].length; j++) {
            const colValueTypes = valueTypes.map((t) => t[j]);
            colValueTypes.shift();
            const isTextCol = colValueTypes.every(
                (t) => t === Excel.RangeValueType.error || t === Excel.RangeValueType.empty || t === Excel.RangeValueType.string
            );
            if (isTextCol) {
                hasCommentsCol = true;
                break;
            }
        }
        return hasCommentsCol;
    }

    protected getDataParsingErrorMsgHtml(isEmptyWorksheet: boolean = false): string {
        let msg = isEmptyWorksheet ? EMPTY_WORKSHEET_MSG : PARSING_ERROR_MSG;
        if (isEmptyWorksheet) {
            msg += "<br> If it does not, please try to <a href='#' onclick='window.location.reload();'>reload your add-in.</a><br>";
        } else {
            // add an option to open the chooser
            msg += `<br> Alternatively, you can insert a visual with sample data from the <a id='${SHOW_CHOOSER_LINK_ID}' href='#'>${this.galleryName}</a><br>`;
        }
        const host = Office.context.host === Office.HostType.Excel ? "excel" : "powerpoint";
        const kbUrl = KNOWLEDGE_BASE_URL.replace("$host", host).replace("$visual", this.visualName);
        const templateUrl = TEMPLATES_URL.replace("$host", host).replace("$visual", this.visualName);
        return `<p>${msg} For more help on data preparation visit our <a href='${kbUrl}' target='_blank' id='error-msg-kb-url'>knowledge base</a>  or download a <a href='${templateUrl}' target='_blank' id='error-msg-template-url'>template file</a>.</p>`;
    }
}
